import React from 'react';
import { Box, Card, CardContent, Grid, Stack, Typography, alpha, useTheme } from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Groups as GroupsIcon,
  RecordVoiceOver as NominatorsIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { NominationStats as NominationStatsModel } from '../nomination.types';

export interface NominationStatsProps {
  stats: NominationStatsModel;
}

const MotionCard = motion.create(Card);

interface TileConfig {
  label: string;
  value: number;
  caption?: string;
  icon: React.ReactNode;
  color: string;
}

/** Four scope-level KPI tiles for the Success Nominations page. */
export const NominationStats: React.FC<NominationStatsProps> = ({ stats }) => {
  const theme = useTheme();

  const tiles: TileConfig[] = [
    {
      label: 'Nominations',
      value: stats.total,
      icon: <TrophyIcon sx={{ fontSize: 36 }} />,
      color: theme.palette.warning.main,
    },
    {
      label: 'Last 30 days',
      value: stats.last30Days,
      caption: 'new nominations',
      icon: <TimelineIcon sx={{ fontSize: 36 }} />,
      color: theme.palette.primary.main,
    },
    {
      label: 'Clients nominated',
      value: stats.uniqueClients,
      caption: 'unique clients',
      icon: <GroupsIcon sx={{ fontSize: 36 }} />,
      color: theme.palette.success.main,
    },
    {
      label: 'Staff nominating',
      value: stats.uniqueNominators,
      caption: 'unique nominators',
      icon: <NominatorsIcon sx={{ fontSize: 36 }} />,
      color: theme.palette.secondary.main,
    },
  ];

  return (
    <Grid container spacing={2.5} sx={{ mb: 3 }}>
      {tiles.map((tile, index) => (
        <Grid item xs={12} sm={6} md={3} key={tile.label}>
          <MotionCard
            role="group"
            aria-label={tile.label}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 * index }}
            elevation={0}
            sx={{
              height: '100%',
              borderRadius: 3,
              border: `1px solid ${alpha(tile.color, 0.22)}`,
              background: `linear-gradient(135deg, ${alpha(tile.color, 0.08)} 0%, ${alpha(tile.color, 0.02)} 100%)`,
            }}
          >
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="overline" color="text.secondary" fontWeight={600}>
                    {tile.label}
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {tile.value}
                  </Typography>
                  {tile.caption && (
                    <Typography variant="caption" color="text.secondary">
                      {tile.caption}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ color: tile.color, opacity: 0.45, display: 'flex' }}>{tile.icon}</Box>
              </Stack>
            </CardContent>
          </MotionCard>
        </Grid>
      ))}
    </Grid>
  );
};

export default NominationStats;
